import { AxiosPromise, AxiosRequestConfig } from 'axios';
import BaseApi from './base/base-api';
import {
  TPostSendSms,
  TPostSendEmail,
  TGetRoomsQuery,
  TGetRoomImageQuery,
  TGetQuestionnairesParams,
  TGetVitalTimelineParams,
  TGetDrugsParams,
  TPostSendNotification,
  TPostSendInvitation,
} from '../../types/member-api';
import { TEvent } from '../../types/events';

class MemberApi extends BaseApi {
  protected authApiURl: string;

  constructor(baseUrl: string) {
    super(baseUrl, {
      allowRefreshToken: true,
      includeAuthorization: true,
    });
    this.authApiURl = process.env.REACT_APP_AUTH_API_URL || '';
  }

  getDoctorProfile = (): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/users/doctor-profile`);

  getProfile = (): Promise<AxiosPromise> => this.axios.get(`${this.baseURL}/users/profile`);

  getProfileById = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/users/profile/${id}`);

  putProfile = (data: any): Promise<AxiosPromise> =>
    this.axios.put(`${this.baseURL}/users/profile`, data);

  putDoctorProfile = (data: any): Promise<AxiosPromise> =>
    this.axios.put(`${this.baseURL}/users/doctor-profile`, data);

  postProfileImage = (profileImage: File): Promise<AxiosPromise> => {
    const formData = new FormData();
    formData.append('profileImage', profileImage);
    return this.axios.post(`${this.baseURL}/users/profile-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  getProfileImage = () => this.axios.get(`${this.baseURL}/users/profile-image`);

  deleteProfileImage = (): Promise<AxiosPromise> =>
    this.axios.delete(`${this.baseURL}/users/profile-image`);

  searchUsers = (data: any): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/users/search`, data);

  getMedInstitutions = (): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/medical-institutions`);

  getPairRequests = (): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/users/pair-request`);

  getPatients = (): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/users/doctor-get-patients`);

  getAllPatients = (data: any): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/users/search`, data);

  patchPairRequest = ({ id, ...data }: any): Promise<AxiosPromise> =>
    this.axios.patch(`${this.baseURL}/users/pair-request/decide/${id}`, data);

  /* TODO: Move */
  putPassword = (data: any): Promise<AxiosPromise> =>
    this.axios.put(`${this.authApiURl}/area/user/change-password`, data);

  getDoctorMessages = (): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/announcement?type=all`);

  postSendEmail = (data: TPostSendEmail): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/announcement/email`, data);

  postSendNotification = (data: TPostSendNotification): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/announcement/push`, data);

  postSendSms = (data: TPostSendSms): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/announcement/sms`, data);

  sendInvitationSms = (data: TPostSendInvitation): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/app-invitation`, data);

  getCountries = (lang: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/countries/${lang}`);

  getRooms = ({ lang, ...rest }: TGetRoomsQuery): Promise<AxiosPromise> => {
    return this.axios.get(`${this.baseURL}/xmpp/rooms`, {
      params: rest,
      headers: {
        'Accept-Language': `${lang}-${lang.toUpperCase()},${lang};`,
      },
    });
  };

  getRoomImage = (query: TGetRoomImageQuery): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/rooms/image`, { params: query });

  getQuestionnaires = (params: TGetQuestionnairesParams): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/questionnaires/answer-doctor`, { params });

  getGeneralInfoById = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/life-id/general-info/${id}`);

  getVitalDataLastUpdateById = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/life-id/general-info/vd-last-update/${id}`);

  getVitalDataById = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/life-id/vital-data/doctor-get-patient/${id}`);

  getCovidScoreByDoctor = (id: string, pageSize = 1): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/life-id/vital-data/covid-score/search/${id}`, {
      id: null,
      source: null,
      from: null,
      to: null,
      page: 1,
      pageSize,
    });

  getMedicalRecordsById = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/software-provider/core/medical-record/${id}`);

  getVitalTimeline = ({ url, ...params }: TGetVitalTimelineParams): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/life-id/vital-data/${url}/timeline`, { params });

  getVitalHistory = ({ url, ...params }: TGetVitalTimelineParams): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/life-id/vital-data/${url}/history`, { params });

  getDrugs = (params: TGetDrugsParams): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/software-provider/core/receipts/request/doctor`, {
      params,
    });

  getDailyReport = (userId: string, dateIso: string) =>
    this.axios.get(`${this.baseURL}/life-id/vital-data/daily-report/${userId}/${dateIso}`);

  getAttachment = (id: string): Promise<AxiosPromise> =>
    this.axios.get(`${this.baseURL}/xmpp/attachment?id=${id}`);

  postAttachment = (attachment: File): Promise<AxiosPromise> => {
    const formData = new FormData();
    formData.append('attachment', attachment);
    return this.axios.post(`${this.baseURL}/xmpp/attachment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  // getBodyMeasurements = (userId: string): Promise<AxiosPromise> =>
  //   this.axios.get(`${this.baseURL}/xmpp/attachment?id=${userId}`);

  getCalendarEvents = (data: any): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/calendar/get-events`, data);

  postCalendarEvent = (data: TEvent): Promise<AxiosPromise> =>
    this.axios.post(`${this.baseURL}/calendar`, data);

  deleteCalendarEvent = (data: any): Promise<AxiosPromise> =>
    this.axios.delete(`${this.baseURL}/calendar`, { data: data });

  editCalendarEvent = (data: TEvent): Promise<AxiosPromise> =>
    this.axios.put(`${this.baseURL}/calendar`, data);

  deleteAllCalendarEvents = (data: any): Promise<AxiosPromise> =>
    this.axios.delete(`${this.baseURL}/calendar/all`, { data: data });

  /**
   * Custom request to wrap instance of axios
   */
  request = <T extends any>(
    url: string,
    config?: Omit<AxiosRequestConfig, 'url' | 'baseURL'>,
  ): AxiosPromise<T> => {
    return this.axios({
      url,
      baseURL: this.baseURL,
      ...config,
    });
  };
}

export const memberApi = new MemberApi(process.env.REACT_APP_MEMBER_API_URL || '');
